import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Target, Table2, Calculator, CalendarRange, Sparkles,
  ArrowRight, ShieldCheck, TrendingUp, Rocket, Check, Crown,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export const googleLogin = () => {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const modules = [
  { icon: Target, title: "SWOT Analysis", desc: "Map internal strengths & weaknesses vs external opportunities & threats.", color: "text-emerald-400" },
  { icon: Table2, title: "Competitor Tracker", desc: "Benchmark rivals on pricing, share and your differentiation edge.", color: "text-sky-400" },
  { icon: Calculator, title: "Pricing Calculator", desc: "Find your price, margin, break-even and projected monthly profit.", color: "text-indigo-400" },
  { icon: CalendarRange, title: "90-Day Action Plan", desc: "A phased launch roadmap from setup to scale, task by task.", color: "text-rose-400" },
];

export default function Landing() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && user) navigate("/dashboard");
  }, [user, loading, navigate]);

  return (
    <div className="min-h-screen grid-bg text-slate-100">
      <nav className="glass sticky top-0 z-50 border-b border-slate-800 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2 font-display font-extrabold text-xl">
          <img src="/swotkit-logo.png" alt="SwotKit" className="h-9 w-9 rounded-lg bg-white p-0.5" />
          SwotKit
        </div>
        <Button data-testid="nav-login-button" onClick={googleLogin} className="rounded-full bg-white text-slate-900 hover:bg-slate-200 font-semibold">
          Sign in with Google
        </Button>
      </nav>

      <section className="max-w-6xl mx-auto px-5 sm:px-6 pt-14 sm:pt-20 pb-16 text-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <div className="inline-flex items-center gap-2 rounded-full border border-indigo-500/30 bg-indigo-500/10 px-4 py-1.5 text-xs sm:text-sm text-indigo-300 mb-6">
            <Sparkles className="h-4 w-4" /> The AI-powered Small Business Starter Kit
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.08] max-w-3xl mx-auto">
            Validate your idea before you <span className="text-indigo-400">bet the business</span> on it.
          </h1>
          <p className="mt-6 text-base sm:text-lg text-slate-300 max-w-2xl mx-auto leading-relaxed">
            SWOT, competitor tracking, pricing math and a 90-day action plan — all in one bundle,
            with an AI co-pilot that drafts the hard parts for you.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button data-testid="hero-cta-button" onClick={googleLogin} size="lg" className="rounded-full cta-btn font-semibold text-base px-7 h-12 w-full sm:w-auto">
              Start free with Google <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <div className="flex items-center gap-2 text-slate-400 text-sm">
              <ShieldCheck className="h-4 w-4 text-emerald-400" /> Free to start — no card needed.
            </div>
          </div>
        </motion.div>

        <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {modules.map((m, i) => (
            <motion.div
              key={m.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 * i }}
              className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6 hover:border-indigo-500/50 hover:-translate-y-1 transition-all duration-300 text-center"
            >
              <m.icon className={`h-7 w-7 mx-auto ${m.color}`} />
              <h3 className="font-display text-lg font-semibold mt-4">{m.title}</h3>
              <p className="text-sm text-slate-400 mt-2 leading-relaxed">{m.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section id="pricing" className="max-w-5xl mx-auto px-5 sm:px-6 pb-16 text-center">
        <h2 className="font-display text-3xl sm:text-4xl font-bold">Simple, honest pricing</h2>
        <p className="text-slate-400 mt-3 max-w-xl mx-auto">Start free. Upgrade once for lifetime full access — <b className="text-slate-200">one-time payment, no subscription</b>.</p>

        <div className="grid sm:grid-cols-2 gap-5 mt-10 text-left">
          <div data-testid="plan-free-card" className="rounded-3xl border border-slate-800 bg-slate-900/60 p-7 flex flex-col">
            <div className="text-sm font-semibold text-slate-300">Free</div>
            <div className="mt-2 font-display text-4xl font-extrabold">$0</div>
            <div className="text-sm text-slate-400">to get started</div>
            <ul className="mt-6 space-y-2.5 flex-1">
              {["1 business plan per day","SWOT basics (low & medium items)","AI SWOT suggestions","Motivation quotes & readiness score","Share link + branded PDF preview"].map((t) => (
                <li key={t} className="flex items-start gap-2 text-sm text-slate-300"><Check className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" /> {t}</li>
              ))}
            </ul>
            <Button data-testid="plan-free-cta" onClick={googleLogin} className="mt-7 rounded-full bg-slate-800 hover:bg-slate-700 text-white w-full h-11">
              Start free
            </Button>
          </div>

          <div data-testid="plan-premium-card" className="relative rounded-3xl border border-orange-500/40 bg-gradient-to-b from-orange-500/10 to-rose-500/5 p-7 flex flex-col">
            <div className="absolute top-5 right-5 inline-flex items-center gap-1 rounded-full bg-orange-500/20 text-orange-300 text-xs font-semibold px-3 py-1"><Crown className="h-3 w-3" /> Best value</div>
            <div className="text-sm font-semibold text-orange-300">Premium</div>
            <div className="mt-2 flex items-end gap-2">
              <div className="font-display text-4xl font-extrabold">$50</div>
              <div className="text-sm text-slate-400 mb-1.5">one-time</div>
            </div>
            <div className="text-sm text-emerald-300">Pay once · full access for life · no monthly fees</div>
            <ul className="mt-6 space-y-2.5 flex-1">
              {["Everything in Free, plus:","Unlimited plans — no daily limit","High-impact SWOT insights unlocked","Competitor matrix with sources","Live pricing research & sensitivity","Full 90-day action plan","AI Model Arena (Claude · Gemini · ChatGPT)","Brand kit + full premium PDF"].map((t, i) => (
                <li key={t} className={`flex items-start gap-2 text-sm ${i === 0 ? "text-slate-400 font-semibold" : "text-slate-200"}`}><Check className="h-4 w-4 text-orange-400 mt-0.5 shrink-0" /> {t}</li>
              ))}
            </ul>
            <Button data-testid="plan-premium-cta" onClick={googleLogin} className="mt-7 rounded-full cta-btn w-full h-11 font-semibold">
              Sign in to upgrade
            </Button>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-5 sm:px-6 pb-24">
        <div className="rounded-3xl border border-slate-800 bg-slate-900/50 p-7 sm:p-12 text-center">
          <h2 className="font-display text-2xl sm:text-3xl font-bold">How SWOT actually works</h2>
          <div className="grid md:grid-cols-2 gap-6 mt-8 text-left">
            <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-6">
              <div className="flex items-center gap-2 text-emerald-400 font-semibold"><ShieldCheck className="h-5 w-5" /> Internal — things you control</div>
              <p className="text-sm text-slate-300 mt-3 leading-relaxed">
                <b>Strengths & Weaknesses.</b> Your skills, money, team, reputation and technology.
                These live inside your business and you can change them.
              </p>
            </div>
            <div className="rounded-2xl border border-sky-500/30 bg-sky-500/5 p-6">
              <div className="flex items-center gap-2 text-sky-400 font-semibold"><TrendingUp className="h-5 w-5" /> External — the market around you</div>
              <p className="text-sm text-slate-300 mt-3 leading-relaxed">
                <b>Opportunities & Threats.</b> Market trends, competitors and regulations.
                You can't control them — but you can plan for them.
              </p>
            </div>
          </div>
          <div className="mt-10 flex items-center justify-center gap-3">
            <Rocket className="h-6 w-6 text-indigo-400" />
            <p className="text-slate-300">Ready in minutes. Sign in and build your first evaluation.</p>
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-800 mt-4">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-slate-400">
          <div className="flex items-center gap-2">
            <img src="/swotkit-logo.png" alt="SwotKit" className="h-6 w-6 rounded bg-white p-0.5" />
            <span>© {new Date().getFullYear()} SwotKit. A brainstorming tool — not professional advice.</span>
          </div>
          <Link data-testid="footer-privacy-link" to="/privacy" className="text-slate-300 hover:text-teal-300 underline underline-offset-4">
            Privacy Policy &amp; Terms of Use
          </Link>
        </div>
      </footer>
    </div>
  );
}
